function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) { try { var info = gen[key](arg); var value = info.value; } catch (error) { reject(error); return; } if (info.done) { resolve(value); } else { Promise.resolve(value).then(_next, _throw); } }

function _asyncToGenerator(fn) { return function () { var self = this, args = arguments; return new Promise(function (resolve, reject) { var gen = fn.apply(self, args); function _next(value) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value); } function _throw(err) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err); } _next(undefined); }); }; }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { isError } from '../is.js';
import { fill, addNonEnumerableProperty } from '../object.js';
import { supportsNativeFetch } from '../supports.js';
import { timestampInSeconds } from '../time.js';
import { GLOBAL_OBJ } from '../worldwide.js';
import { addHandler, maybeInstrument, triggerHandlers } from './handlers.js';
/**
 * Add an instrumentation handler for when a fetch request happens.
 * The handler function is called once when the request starts and once when it ends,
 * which can be identified by checking if it has an `endTimestamp`.
 *
 * Use at your own risk, this might break without changelog notice, only used internally.
 * @hidden
 */

function addFetchInstrumentationHandler(handler, skipNativeFetchCheck) {
  var type = 'fetch';
  addHandler(type, handler);
  maybeInstrument(type, () => instrumentFetch(undefined, skipNativeFetchCheck));
}
/**
 * Add an instrumentation handler for long-lived fetch requests, like consuming server-sent events (SSE) via fetch.
 * The handler will resolve the request body and emit the actual `endTimestamp`, so that the
 * span can be updated accordingly.
 *
 * Only used internally
 * @hidden
 */


function addFetchEndInstrumentationHandler(handler) {
  var type = 'fetch-body-resolved';
  addHandler(type, handler);
  maybeInstrument(type, () => instrumentFetch(streamHandler));
}

function instrumentFetch(onFetchResolved) {
  var skipNativeFetchCheck = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

  if (skipNativeFetchCheck && !supportsNativeFetch()) {
    return;
  }

  fill(GLOBAL_OBJ, 'fetch', function (originalFetch) {
    return function () {
      // We capture the error right here and not in the Promise error callback because Safari (and probably other
      // browsers too) will wipe the stack trace up to this point, only leaving us with this file which is useless.
      // NOTE: If you are a Sentry user, and you are seeing this stack frame,
      //       it means the error, that was caused by your fetch call did not
      //       have a stack trace, so the SDK backfilled the stack trace so
      //       you can see which fetch call failed.
      var virtualError = new Error();

      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      var {
        method,
        url
      } = parseFetchArgs(args);
      var handlerData = {
        args,
        fetchData: {
          method,
          url
        },
        startTimestamp: timestampInSeconds() * 1000,
        // // Adding the error to be able to fingerprint the failed fetch event in HttpClient instrumentation
        virtualError
      }; // if there is no callback, fetch is instrumented directly

      if (!onFetchResolved) {
        triggerHandlers('fetch', _objectSpread({}, handlerData));
      } // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access


      return originalFetch.apply(GLOBAL_OBJ, args).then( /*#__PURE__*/function () {
        var _ref = _asyncToGenerator(function* (response) {
          if (onFetchResolved) {
            onFetchResolved(response);
          } else {
            triggerHandlers('fetch', _objectSpread(_objectSpread({}, handlerData), {}, {
              endTimestamp: timestampInSeconds() * 1000,
              response
            }));
          }

          return response;
        });

        return function (_x) {
          return _ref.apply(this, arguments);
        };
      }(), error => {
        triggerHandlers('fetch', _objectSpread(_objectSpread({}, handlerData), {}, {
          endTimestamp: timestampInSeconds() * 1000,
          error
        }));

        if (isError(error) && error.stack === undefined) {
          // NOTE: If you are a Sentry user, and you are seeing this stack frame,
          //       it means the error, that was caused by your fetch call did not
          //       have a stack trace, so the SDK backfilled the stack trace so
          //       you can see which fetch call failed.
          error.stack = virtualError.stack;
          addNonEnumerableProperty(error, 'framesToPop', 1);
        } // NOTE: If you are a Sentry user, and you are seeing this stack frame,
        //       it means the sentry.javascript SDK caught an error invoking your application code.
        //       This is expected behavior and NOT indicative of a bug with sentry.javascript.


        throw error;
      });
    };
  });
}

function resolveResponse(_x2, _x3) {
  return _resolveResponse.apply(this, arguments);
}

function _resolveResponse() {
  _resolveResponse = _asyncToGenerator(function* (res, onFinishedResolving) {
    if (res !== null && res !== void 0 && res.body) {
      yield* function* () {
        var body = res.body;
        var responseReader = body.getReader(); // Define a maximum duration after which we just cancel

        var maxFetchDurationTimeout = setTimeout(() => {
          body.cancel().then(null, () => {// noop
          });
        }, 90 * 1000 // 90s
        );
        var readingActive = true;

        while (readingActive) {
          var chunkTimeout = void 0;

          try {
            // abort reading if read op takes more than 5s
            chunkTimeout = setTimeout(() => {
              body.cancel().then(null, () => {// noop on error
              });
            }, 5000); // This .read() call will reject/throw when we abort due to timeouts through `body.cancel()`

            var {
              done
            } = yield responseReader.read();
            clearTimeout(chunkTimeout);

            if (done) {
              onFinishedResolving();
              readingActive = false;
            }
          } catch (error) {
            readingActive = false;
          } finally {
            clearTimeout(chunkTimeout);
          }
        }

        clearTimeout(maxFetchDurationTimeout);
        responseReader.releaseLock();
        body.cancel().then(null, () => {// noop on error
        });
      }();
    }
  });
  return _resolveResponse.apply(this, arguments);
}

function streamHandler(response) {
  // clone response for awaiting stream
  var clonedResponseForResolving;

  try {
    clonedResponseForResolving = response.clone();
  } catch (_unused) {
    return;
  } // eslint-disable-next-line @typescript-eslint/no-floating-promises


  resolveResponse(clonedResponseForResolving, () => {
    triggerHandlers('fetch-body-resolved', {
      endTimestamp: timestampInSeconds() * 1000,
      response
    });
  });
}

function hasProp(obj, prop) {
  return !!obj && typeof obj === 'object' && !!obj[prop];
}

function getUrlFromResource(resource) {
  if (typeof resource === 'string') {
    return resource;
  }

  if (!resource) {
    return '';
  }

  if (hasProp(resource, 'url')) {
    return resource.url;
  }

  if (resource.toString) {
    return resource.toString();
  }

  return '';
}
/**
 * Parses the fetch arguments to find the used Http method and the url of the request.
 * Exported for tests only.
 */


function parseFetchArgs(fetchArgs) {
  if (fetchArgs.length === 0) {
    return {
      method: 'GET',
      url: ''
    };
  }

  if (fetchArgs.length === 2) {
    var [url, options] = fetchArgs;
    return {
      url: getUrlFromResource(url),
      method: hasProp(options, 'method') ? String(options.method).toUpperCase() : 'GET'
    };
  }

  var arg = fetchArgs[0];
  return {
    url: getUrlFromResource(arg),
    method: hasProp(arg, 'method') ? String(arg.method).toUpperCase() : 'GET'
  };
}

export { addFetchEndInstrumentationHandler, addFetchInstrumentationHandler, parseFetchArgs };